// ============================================================================
// Trainer-side weekly-goal hooks (Wave 3).
// ----------------------------------------------------------------------------
//   • useClientWeeklyGoal — one client's goal for a week (defaults to current).
//   • useUpsertWeeklyGoal — PUT the goal (idempotent per client+week). On success
//     invalidates the weekly-goals root (so the form reflects the saved row) AND
//     the nutrition root (so the trainer's nutrition panel re-resolves coach-vs-
//     auto targets — a set goal flips `source` to 'coach' for that week).
// Both also live-sync: a `weekly_goal` broadcast maps (ENTITY_INVALIDATIONS) to
// those same roots, so a change on another tab/device refetches without reload.
// ============================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk, weeklyGoalsApi } from "../api";
import type { WeeklyGoalInput } from "../api";
import type { WeeklyGoalCalendarRange } from "../api/weeklyGoals";

/** One owned client's weekly goal for `week` (defaults to the current week). */
export function useClientWeeklyGoal(
  clientId: number | undefined,
  week?: string,
) {
  return useQuery({
    queryKey: qk.weeklyGoals.forClient(clientId ?? -1, week),
    queryFn: ({ signal }) =>
      weeklyGoalsApi.forClient(clientId as number, week, signal),
    enabled: clientId !== undefined && clientId >= 0,
  });
}

/**
 * All of THIS trainer's weekly goals as week banners — the lime week-level layer
 * merged onto the trainer all-clients calendar grid. Live-syncs via the
 * `weekly_goal` broadcast (ENTITY_INVALIDATIONS → weekly-goals root).
 */
export function useWeeklyGoalCalendar(range: WeeklyGoalCalendarRange = {}) {
  return useQuery({
    queryKey: qk.weeklyGoals.calendar(range.start, range.end),
    queryFn: ({ signal }) => weeklyGoalsApi.calendar(range, signal),
  });
}

/** Upsert an owned client's weekly goal; refreshes goal + nutrition queries. */
export function useUpsertWeeklyGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: number;
      body: WeeklyGoalInput;
    }) => weeklyGoalsApi.upsert(clientId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.weeklyGoals.root });
      void qc.invalidateQueries({ queryKey: qk.nutrition.root });
    },
  });
}
